import React from 'react';
import {View, StyleSheet} from 'react-native';
import Localized from 'src/constants/AppStrings';
import Styles from '../../../Styles';
import AVText from '../../../elements/AVText';
import DiscountTag from 'src/components/img/svg/DiscountTag';
import Util from 'src/Util';
import {scanAndPayTemplate} from '../templates/scanandPayThemeHelper';
import DiscountCodeInput from 'src/components/screens/scanAndPay/DiscountCodeInput';
import EditLocation from 'src/components/img/svg/EditLocation';
import AVTouchableOpacity from 'src/components/elements/AVTouchableOpacity';

type UnlockDiscountCanteenProps = {
  location?: {
    locationFeatures?: {
      is365PayAfterTaxPromo?: boolean;
    };
  };
  consumerRoleButton?: string;
  addDiscountToCart?: () => void;
  hasConsumerRoles?: boolean;
  consumerRoleIdentifier?: {
    value?: string;
  };
  validateDiscountCode?: () => void;
  discountCodeButtonAction?: () => void;
  saveDiscountCode?: (discountCode?: string) => void;
  showErrorMsg?: boolean;
  showSuccessMsg?: boolean;
  changeToDefaultDiscount?: boolean;
  onChangeBtnPress?: () => void;
};

const getActiveColor = (showErrorMsg: boolean) => {
  return showErrorMsg
    ? Styles.Colors.PayNew.error
    : Styles.Colors.PayNew.darkHuesBase04;
};

const getBorderColor = (showErrorMsg: boolean) => {
  return showErrorMsg
    ? Styles.Colors.PayNew.error
    : Styles.Colors.PayNew.neutralHuesBase09;
};

const UnlockDiscountCanteen: React.FC<UnlockDiscountCanteenProps> = ({
  hasConsumerRoles,
  consumerRoleIdentifier,
  discountCodeButtonAction,
  showErrorMsg,
  saveDiscountCode,
  showSuccessMsg,
  changeToDefaultDiscount,
  onChangeBtnPress,
}) => {
  const [saveCode, setSaveCode] = React.useState(
    consumerRoleIdentifier?.value ?? null,
  );
  const handleTextChange = (text) => {
    setSaveCode(text);
    /** when length 0 change the textinput to default */
    if (text.length === 0) {
      onChangeBtnPress();
    }
  };
  const discountLabel =
    showSuccessMsg && consumerRoleIdentifier?.value
      ? Localized.Labels.discount_code_saved_tap_edit_icon
      : Localized.Labels.saved_discount_from_last_visit_tap_edit_button;

  if (hasConsumerRoles) {
    if (consumerRoleIdentifier?.value && !changeToDefaultDiscount) {
      return (
        <View style={styles.discountApplied}>
          <View style={styles.discountRow}>
            <View style={styles.appliedDiscountView}>
              <View style={styles.discountContent}>
                <DiscountTag
                  style={{marginTop: 2, alignSelf: 'center'}}
                  fill={Styles.Colors.Canteen.primary}
                />
                <AVText
                  testID={'qa_DynamicDiscountCode_txt'}
                  maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
                  accessibilityLabel={consumerRoleIdentifier?.value}
                  aria-label={consumerRoleIdentifier?.value}
                  style={styles.discountText}
                >
                  {Util.removeTrailingEscapeSequencesAndSpaces(
                    consumerRoleIdentifier?.value,
                  )}
                </AVText>
              </View>
              <AVTouchableOpacity
                onPress={onChangeBtnPress}
                style={styles.editStyle}
                accessibilityLabel={Localized.Buttons.change}
                aria-label={Localized.Buttons.change}
                accessible
                testID="qa_Checkout.ChangeDiscount_btn"
              >
                <EditLocation color={Styles.Colors.Canteen.primary} />
              </AVTouchableOpacity>
            </View>
          </View>
          <AVText
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
            accessibilityLabel={consumerRoleIdentifier?.value}
            aria-label={consumerRoleIdentifier?.value}
            style={styles.savedText}
            accessible
            testID="qa_Checkout.PromotionText_txt"
            nativeID="qa_Checkout.PromotionText_txt"
          >
            {discountLabel}
          </AVText>
        </View>
      );
    }

    return (
      <View style={styles.wrapper}>
        <View style={{flex: 1}}>
          <DiscountCodeInput
            label={Util.secondLetterCapitalize(Localized.Labels.discount_code)}
            value={saveCode}
            onChangeText={handleTextChange}
            onScanQr={discountCodeButtonAction}
            onApply={() => saveDiscountCode(saveCode)}
            showError={showErrorMsg}
            errorMsg={Localized.Errors.invalid_code}
            errorTextStyle={styles.errorText}
            activeColor={getActiveColor(showErrorMsg)}
            outlineStyle={{
              borderColor: getBorderColor(showErrorMsg),
              borderRadius: Styles.Spacing.m2,
              borderWidth: 1,
            }}
          />
        </View>
      </View>
    );
  }

  return null;
};

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    flexDirection: 'row',
    marginBottom: Styles.Spacing.m2,
    justifyContent: 'space-between',
    position: 'relative',
    marginTop: Styles.Spacing.m2,
  },
  discountApplied: {
    flex: 1,
    marginTop: Styles.Spacing.m3,
    marginBottom: Styles.Spacing.m2,
  },
  discountRow: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  appliedDiscountView: {
    flexDirection: 'row',
    alignSelf: 'center',
    paddingHorizontal: Styles.Spacing.m2 + Styles.Spacing.m1,
    backgroundColor: 'rgba(64, 159, 104, 0.15)',
    paddingVertical: Styles.Spacing.m2,
    borderRadius: Styles.Spacing.m1,
  },
  discountText: {
    marginTop: 2,
    fontSize: Styles.Fonts.f1,
    fontFamily: scanAndPayTemplate.textFontFamily,
    color: Styles.Colors.Canteen.primary,
    fontWeight: '400',
    marginLeft: Styles.Spacing.m2,
    paddingRight: Styles.Spacing.m2 + Styles.Spacing.m1,
    flexWrap: 'wrap',
  },
  buttonStyle: {
    height: Styles.Spacing.m4 + Styles.Spacing.m1 + 2,
    borderWidth: 2,
    paddingHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
    backgroundColor: Styles.Colors.PayNew.white01,
  },
  savedText: {
    fontSize: Styles.Fonts.f8,
    color: Styles.Colors.Canteen.green,
    fontWeight: '400',
    fontFamily: scanAndPayTemplate.textFontFamily,
    marginTop: Styles.Spacing.m2 + 2,
  },
  errorText: {
    fontSize: Styles.Fonts.f8,
    color: Styles.Colors.PayNew.error,
    alignSelf: 'flex-start',
    marginRight: Styles.Spacing.m3,
    fontFamily: scanAndPayTemplate.textFontFamily,
  },
  editStyle: {
    marginLeft: Styles.Spacing.m3,
    justifyContent: 'center',
  },
  discountContent: {
    flexDirection: 'row',
    flexShrink: 1,
    alignItems: 'center',
  },
});

export default UnlockDiscountCanteen;
